import React, { useEffect, useRef } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { AttributeWindowLinkModel } from "../api/drawingApi";
import AttributeWindowLinkEdit from "./AttributeWindowLinkEdit";
import { Problem } from "../api/backendApi";

interface AttributeWindowPageEditProps {
  title: string;
  text: string;
  readOnly: boolean;
  links: AttributeWindowLinkModel[];
  otherWindows: string[];
  onTitleChange: (newTitle: string) => void;
  onTextChange: (newText: string) => void;
  onErrorChange: (errors: string[]) => void;
  onLinksChange: (links: AttributeWindowLinkModel[]) => void;
  onError: (error: Problem | string | undefined) => void;
}

function AttributeWindowPageEdit({
  otherWindows,
  title,
  text,
  readOnly,
  links,
  onTitleChange,
  onTextChange,
  onErrorChange,
  onLinksChange,
  onError,
}: AttributeWindowPageEditProps) {
  const [errors, setErrors] = React.useState<string[]>([]);
  const [titleError, setTitleError] = React.useState(!readOnly && title.trim() === "");

  const svgText = useRef<SVGTextElement>(null);

  const overflows = (value: string): boolean => {
    if (!svgText || !svgText.current) return false;

    svgText.current.innerHTML = value.replace(/</g, "&lt;");

    const result = svgText.current.getComputedTextLength() > 278;

    return result;
  };

  useEffect(() => {
    var textErrors: string[] = [];
    const lines: string[] = text.toString().split(/(?:\r?\n|<NL>)/);

    if(!readOnly) {
      for (var line of lines)  {
        if (overflows(line)) {
          textErrors.push(line);
        }
      }
    }

    const hasTitleError = !readOnly && title.trim() === "";
    setTitleError(hasTitleError);
    setErrors(textErrors);
    onErrorChange([...textErrors, ...(hasTitleError ? ["title"] : [])]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, title, readOnly]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const hasTitleError = value.trim() === "";
    setTitleError(hasTitleError);
    onTitleChange(value);
  };

  const handleChange = (event: any) => {
    const value = event.target.value;
    var textErrors: string[] = [];
    const lines: string[] = value.toString().split(/(?:\r?\n|<NL>)/);

    if(!readOnly) {
      for (var line of lines)  {
        if (overflows(line)) {
          textErrors.push(line);
        }
      }
    }

    setErrors(textErrors);
    onTextChange(value);
  }  

  return (
    <Row>
      <Col>
        <Form.Group>
          <Form.Label>Title</Form.Label>
          <Form.Control
            value={title}
            readOnly={readOnly}
            isInvalid={titleError}
            onChange={handleTitleChange}
          />
          <Form.Control.Feedback type="invalid">
            Title is required.
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group>
          <Form.Label>Text</Form.Label>
          <Form.Control
            style={{ fontFamily: "Arial", fontSize: "0.92rem", overflowX: "scroll", whiteSpace: "nowrap", maxWidth: "696px" }}
            as="textarea"
            defaultValue={text}
            readOnly={readOnly}
            rows={15}
            isInvalid={errors.length > 0}
            onChange={(e) => handleChange(e)}
          />
          <Form.Control.Feedback type="invalid">
            {errors.length > 0 &&
              `${errors.length} line(s) that are too long. Please ensure each line fits within the attribute window.`}
          </Form.Control.Feedback>
          <svg
            id="testsvg"
            viewBox="0 0 278 30"
            style={{ position: "absolute", zIndex: -1 }}
          >
            <text
              ref={svgText}
              style={{
                whiteSpace: "pre",
                strokeWidth: "0.03px",
                stroke: "black",
                fill: "black",
                fontFamily: "Arial",
                fontSize: "4.5pt",
              }}
            >
            </text>
          </svg>
        </Form.Group>
      </Col>
      <Col md="auto" style={{ width: "30%" }}>
        <AttributeWindowLinkEdit
          links={links}
          readOnly={readOnly}
          onChange={(l) => onLinksChange(l)}
          onError={(e) => onError(e)}
          otherWindows={otherWindows}
        />
      </Col>
    </Row>
  );
}

export default AttributeWindowPageEdit;
